import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./style.css";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowDropDownCircleIcon from "@mui/icons-material/ArrowDropDownCircle";
import {
  Box,
  Typography,
  Grid,
  FormHelperText,
  FormLabel,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  InputAdornment,
} from "@mui/material";
import { Formik, useFormik } from "formik";
import * as yup from "yup";
import CustomTextField from "../../Components/TextField";
import ReactLoading from "react-loading";
import { useErrorHandler } from "../../hooks";
import { useDispatch, useSelector } from "react-redux";
import {
  CLEAR_BULLETIN_SUCCESS,
  createBulletinDataAction,
  previewBulletinDataAction,
  publishedBulletinStatus,
  updateBulletinDraftDetailAction,
} from "../../Redux/Actions/BulletinAction";
import HelmetReact from "../../Components/Helmet";
import useToggle from "../../hooks/use-toggle";

import MyEditor from "../../Components/MyEditor";

const CreateBulletin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [coverImagePreview, setCoverImagePreview] = useState("");
  const [contentImagePreview, setContentImagePreview] = useState("");
  const fileInputRef1 = useRef(null);
  const fileInputRef2 = useRef(null);
  const { setMsgConfig } = useErrorHandler();
  const [editForm, setEditForm] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [viewOn, setViewOn] = useToggle(false);
  const [editorLoaded, setEditorLoaded] = useState(false);
  const [data, setData] = useState("");
  const [navigateRoute, setNavigateRoute] = useState("SANDBOX");
  const { bulletinDetail, isEdit, loading } = useSelector(
    (state) => state.Bulletin
  );
  const goBack = () => {
    setEditForm(false);
    dispatch({ type: CLEAR_BULLETIN_SUCCESS });
    navigate("/bulletin", { state: navigateRoute });
  };

  const formik = useFormik({
    initialValues: {
      Id: bulletinDetail?.Id ? bulletinDetail?.Id : "",
      title: bulletinDetail?.title ? bulletinDetail?.title : "",
      contributor_name: bulletinDetail?.contributor_name
        ? bulletinDetail?.contributor_name
        : "",
      cover_img: bulletinDetail?.cover_img ? bulletinDetail?.cover_img : "",
      short_desc: bulletinDetail?.short_desc
        ? bulletinDetail?.short_desc
        : "",
      full_desc: bulletinDetail?.full_desc
        ? bulletinDetail?.full_desc
        : "",
      content_file: !isEdit ? bulletinDetail?.content_file
      : bulletinDetail?.content_file ? bulletinDetail?.content_file : "",
      content_file_duration: bulletinDetail?.content_file_duration
        ? bulletinDetail?.content_file_duration
        : null,
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      title: yup
        .string()
        .matches(
          /^\S.*/,
          "* This field cannot contain whitepaces at first letter"
        )
        .required("Please enter title"),
      contributor_name: yup
        .string()
        .matches(
          /^\S.*/,
          "* This field cannot contain whitepaces at first letter"
        )
        .required("Please enter contributor name"),
      cover_img: yup
        .mixed()
        .required("A file is required")
        .test("fileFormat", "JPEG JPG AND PNG Files only", (value) => {
          const file = new File([value], "tempimage", {
            type: value?.type || "image/jpg",
          });
          return (
            !file ||
            (file &&
              ["image/jpeg", "image/png", "image/jpg"].includes(file.type))
          );
        }),
      short_desc: yup
        .string()
        .matches(
          /^\S.*/,
          "* This field cannot contain whitepaces at first letter"
        )
        .max(130, "Maxminum 130 letters description")
        .required("Please enter description in short"),
      full_desc: yup.string().required("Please enter full description"),
      content_file: yup
        .mixed()
        .required("A file is required")
        .test("fileFormat", "JPEG JPG PNG AND VIDEO Files only", (value) => {
          const file = new File([value], "tempimage", {
            type: value?.type || "image/jpg" || value?.type || "video/mp4",
          });
          return (
            !file ||
            (file &&
              ["image/jpeg", "image/png", "image/jpg", "video/mp4"].includes(
                file.type
              ))
          );
        }),
    }),
    onSubmit: (values, { resetForm }) => {
      if (!isEdit) {        
        dispatch(previewBulletinDataAction(formik.values));
        navigate("/bulletin/create/preview");        
      } else {        
        dispatch(previewBulletinDataAction(formik.values));
        setEditForm(false);
        navigate("/bulletin/create/preview");        
      }
    },
  });

  useEffect(() => {
    setEditorLoaded(true);
  }, []);

  const handleKeyPress1 = (event) => {
    if (event?.key == "Enter") {
      fileInputRef1?.current?.click();
    }
  };

  const handleKeyPress2 = (event) => {
    if (event?.key == "Enter") {
      fileInputRef2?.current?.click();
    }
  };

  useEffect(() => {
    if (isEdit) {
      setNavigateRoute("SANDBOX");
      setEditForm(true);
      typeof bulletinDetail?.cover_img === "string" && setCoverImagePreview(
        bulletinDetail?.cover_img ? bulletinDetail?.cover_img : ""
      );
      typeof bulletinDetail?.cover_img === "object" && setCoverImagePreview(
        bulletinDetail?.cover_img ? URL.createObjectURL(bulletinDetail?.cover_img ): ""
      );
      setContentImagePreview(
        bulletinDetail?.content_file ? bulletinDetail?.content_file : ""
      );
    }
    else{
      setEditForm(false);
      setCoverImagePreview(
        bulletinDetail?.cover_img ? URL.createObjectURL(bulletinDetail?.cover_img ): ""
      );
    }
  }, [bulletinDetail]);

  const [duration, setDuration] = useState(null);

  const handleVideoFileChange = (e) => {
    const fileObj = e.target.files[0];
    if (fileObj.type === "video/mp4") {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        formik.setFieldValue(
          "content_file_duration",
          Math.floor(video.duration)
        );
      };
      video.src = URL.createObjectURL(fileObj);
    } else {
      formik.setFieldValue("content_file_duration", null);
    }
  };

  return (
    <>
      <HelmetReact name="Bulletin" />
      <Box className="d-details-box">
        <Typography className="web-title">
          {editForm ? "UPDATE BULLETIN" : "CREATE BULLETIN"}
        </Typography>
        <Grid container spacing={2}>
          <Grid item lg={6} md={6} sm={6} xs={12}>
            <Button onClick={() => goBack()} className="back-btn">
              <ArrowBackIosIcon
                size="large"
                edge="start"
                aria-label="menu"
                sx={{
                  mr: 2,
                  color: "grey",
                  flexGrow: 0,
                  cursor: "pointer",
                  mb: 2,
                }}
              />
            </Button>
          </Grid>
        </Grid>
        <Box>
          <Grid container spacing={2}>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Box>
                <CustomTextField
                  fieldType="textInput"
                  label="Bulletin title"
                  name="title"
                  required
                  fullWidth
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={"Enter Your bulletin title"}
                  error={
                    formik.touched.title &&
                      formik.errors.title
                      ? true
                      : false
                  }
                />
                {formik.touched.title &&
                  formik.errors.title && (
                    <FormHelperText className="error_message">
                      {formik.errors.title}
                    </FormHelperText>
                  )}
              </Box>
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Box>
                <CustomTextField
                  fieldType="textInput"
                  label="Contributor name"
                  name="contributor_name"
                  required
                  fullWidth
                  value={formik.values.contributor_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={"Enter Your contributor name"}
                  error={
                    formik.touched.contributor_name &&
                      formik.errors.contributor_name
                      ? true
                      : false
                  }
                />
                {formik.touched.contributor_name &&
                  formik.errors.contributor_name && (
                    <FormHelperText className="error_message">
                      {formik.errors.contributor_name}
                    </FormHelperText>
                  )}
              </Box>
            </Grid>
            <Grid item lg={6} md={6} sm={6} xs={12}>
              <Box>
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <FormLabel id="demo-controlled-radio-buttons-group">
                    Cover Image *
                  </FormLabel>
                  {formik.touched.cover_img && formik.errors.cover_img && (
                    <FormHelperText className="error_message">
                      {formik.errors.cover_img}
                    </FormHelperText>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant="contained"
                    component="label"
                    onKeyDown={handleKeyPress1}
                    className="program-cover-img-btn"
                    endIcon={<ArrowDropDownCircleIcon />}
                  >
                    Select File
                    <input
                      name="cover_img"
                      accept="image/*"
                      id="contained-button-file"
                      ref={fileInputRef1}
                      type="file"
                      hidden
                      onChange={(e) => {
                        const fileReader = new FileReader();
                        fileReader.onload = () => {
                          formik.setTouched({
                            ...formik.touched.cover_img,
                            cover_img: true,
                          });
                          if (fileReader.readyState === 2) {
                            formik.setFieldValue(
                              "cover_img",
                              e.target.files[0]
                            );
                            setCoverImagePreview(fileReader.result);
                          }
                        };
                        e.target.files[0] &&
                          fileReader.readAsDataURL(e.target.files[0]);
                      }}
                    />
                  </Button>
                </Grid>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Typography>16:9</Typography>
              <img src={coverImagePreview} width="200px" height="70px" alt="" />
            </Grid>
            <Grid item xs={3}>
              <Typography>1:1</Typography>
              <img
                src={coverImagePreview}
                width="180px"
                height="150px"
                alt=""
              />
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Box>
                <CustomTextField
                  fieldType="textInput"
                  label="Short description"
                  name="short_desc"
                  required
                  fullWidth
                  value={formik.values.short_desc}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  inputProps={{ maxLength: 130 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment disableTypography position="end">
                        {`${formik.values.short_desc?.length}/130`}
                      </InputAdornment>
                    ),
                  }}
                  placeholder={"Enter Your short description"}
                  error={
                    formik.touched.short_desc &&
                      formik.errors.short_desc
                      ? true
                      : false
                  }
                />
                {formik.touched.short_desc &&
                  formik.errors.short_desc && (
                    <FormHelperText className="error_message">
                      {formik.errors.short_desc}
                    </FormHelperText>
                  )}
              </Box>
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Box>
                <MyEditor
                  name="full_desc"
                  value={formik.values.full_desc}
                  onChange={(data) => {
                    formik.setFieldValue("full_desc", data);
                  }}
                />
                {formik.touched.full_desc &&
                  formik.errors.full_desc && (
                    <FormHelperText className="error_message">
                      {formik.errors.full_desc}
                    </FormHelperText>
                  )}
              </Box>
            </Grid>

            <Grid item lg={6} md={6} sm={6} xs={12}>
              <Box>
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <FormLabel id="demo-controlled-radio-buttons-group">
                    Bulletin Content file *
                  </FormLabel>
                  {formik.touched.content_file &&
                    formik.errors.content_file && (
                      <FormHelperText className="error_message">
                        {formik.errors.content_file}
                      </FormHelperText>
                    )}
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant="contained"
                    component="label"
                    onKeyDown={handleKeyPress2}
                    className="program-cover-img-btn"
                    endIcon={<ArrowDropDownCircleIcon />}
                  >
                    Select File
                    <input
                      name="content_file"
                      id="contained-button-file"
                      type="file"
                      ref={fileInputRef2}
                      hidden
                      onChange={(e) => {
                        formik.setTouched({
                          ...formik.touched.content_file,
                          content_file: true,
                        });
                        const fileReader = new FileReader();
                        fileReader.onload = () => {
                          if (fileReader.readyState === 2) {
                            handleVideoFileChange(e);
                            formik.setFieldValue(
                              "content_file",
                              e.target.files[0]
                            );
                            setContentImagePreview(e.target.files[0]);
                          }
                        };
                        e.target.files[0] &&
                          fileReader.readAsDataURL(e.target.files[0]);
                      }}
                    />
                  </Button>
                </Grid>
              </Box>
            </Grid>

            {isEdit == true &&
              typeof contentImagePreview === "string" &&
              (contentImagePreview.toLowerCase().endsWith(".mp4") ? (
                <Grid item xs={3}>
                  <iframe src={contentImagePreview} title="trailer" />
                </Grid>
              ) : (
                (contentImagePreview.toLowerCase().endsWith(".jpg") ||
                  contentImagePreview.toLowerCase().endsWith(".png") ||
                  contentImagePreview.toLowerCase().endsWith(".jpeg")) && (
                  <Grid item xs={3}>
                    <img
                      src={contentImagePreview}
                      alt=""
                      width="200px"
                      height="200px"
                    />
                  </Grid>
                )
              ))}

            {typeof formik?.values?.content_file == "object" &&
              formik?.values?.content_file?.type == "video/mp4" ? (
              <Grid item xs={3}>
                <iframe
                  src={URL.createObjectURL(
                    formik?.values?.content_file
                  )}
                  title="content_file"
                />
              </Grid>
            ) : formik.values?.content_file?.type == "image/jpg" ||
              formik.values?.content_file?.type == "image/jpeg" ||
              formik.values?.content_file?.type == "image/png" ? (
              <Grid item xs={3}>
                <img
                  src={URL.createObjectURL(
                    formik.values?.content_file
                  )}
                  alt=""
                  width="200px"
                  height="200px"
                />
              </Grid>
            ) : (
              ""
            )}

            <Grid item xs={12} sx={{ textAlign: "end" }}>
              <Button
                sx={{
                  background: "#000",
                  borderRadius: "70px",
                }}
                variant="contained"
                className="next-btn"
                onClick={formik.handleSubmit}
              >
                {loading ? (
                  <Box className="outer_loader">
                    <ReactLoading
                      type={"bubbles"}
                      height="60px"
                      width="30px"
                      color="#FFFFFF"
                    />
                  </Box>
                ) : !editForm ? (
                  "Preview"
                ) : (
                  "Update"
                )}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default CreateBulletin;
